import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const validateToken = useCallback(async () => {
    try {
      if (authService.isAuthenticated()) {
        const profile = await authService.getProfile();
        if (!profile) {
          throw new Error('Invalid token');
        }
        // Token is still valid, update user if needed
        setUser(profile);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.warn('Token validation failed:', error);
      await logout();
    }
  }, []);

  const initAuth = useCallback(async () => {
    try {
      if (authService.isAuthenticated()) {
        const profile = await authService.getProfile();
        setUser(profile);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      // Clear invalid tokens
      await logout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initAuth();
    // Set up periodic token validation
    const interval = setInterval(validateToken, 5 * 60 * 1000); // Check every 5 minutes
    return () => clearInterval(interval);
  }, [initAuth, validateToken]);

  const login = async (credentials) => {
    try {
      setLoading(true);
      const { admin, token } = await authService.login(credentials);
      setUser(admin);
      setIsAuthenticated(true);
      return { success: true, admin, token };
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data) => {
    try {
      const updatedUser = await authService.updateProfile(data);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (data) => {
    try {
      return await authService.changePassword(data);
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    isAuthenticated,
    login,
    logout,
    updateProfile,
    changePassword,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}